import { Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Sparkles, CheckCircle2, MessageSquare, Search, Shield } from 'lucide-react';

const Home = () => {
  const { config } = useAuth();
  const free = config?.free_mode;
  return (
    <div className="min-h-screen">
      <div 
        className="relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1769181417562-be594f91fcc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxpbmslMjB3YXNoJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzQ2NTM3Mjh8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.05,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none'
        }}
      />
      
      <div className="relative z-10">
        <nav className="px-6 md:px-12 lg:px-24 py-6 flex justify-between items-center border-b border-[#E5E0D8]">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
          <Link to="/login">
            <Button variant="ghost" className="rounded-sm" data-testid="nav-login-btn">
              登入
            </Button>
          </Link>
        </nav>

        <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="inline-flex items-center border border-[#C93B2B] text-[#C93B2B] px-2 py-1 text-xs rounded-none font-serif tracking-widest">
              先驗證，再選擇
            </div>
            {free && (
              <div className="inline-flex items-center bg-[#C93B2B] text-white px-2 py-1 text-xs rounded-none font-serif tracking-widest" data-testid="free-badge">
                推廣期免費
              </div>
            )}
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-serif text-[#1A1A1A] leading-tight max-w-4xl mx-auto mb-8">
            讓多位命理師回答你的驗證問題<br />比較後再決定
          </h1>
          
          <p className="text-base leading-relaxed text-[#4A4A4A] font-sans max-w-2xl mx-auto mb-12">
            不用盲目選擇。先提交你的出生資料和驗證問題，讓命理師展現實力，你再從中挑選最適合的一位。
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login?role=client">
              <Button 
                className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm px-8 py-6 text-lg shadow-sm"
                data-testid="cta-client-btn"
              >
                <Sparkles className="mr-2" strokeWidth={1.5} />
                我要算命
              </Button>
            </Link>
            <Link to="/login?role=astrologer">
              <Button 
                variant="outline"
                className="border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#F5F0E6] rounded-sm px-8 py-6 text-lg"
                data-testid="cta-astrologer-btn"
              >
                我是命理師
              </Button>
            </Link>
          </div>
        </section>

        <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-white">
          <p className="text-xs tracking-[0.2em] uppercase text-[#C93B2B] font-bold font-sans text-center mb-12">
            如何運作
          </p>
          
          <div className="max-w-4xl mx-auto space-y-16">
            <div className="flex gap-8 items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-sm bg-[#C93B2B] text-white flex items-center justify-center font-serif text-xl">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-serif text-[#1A1A1A] mb-3">提交求測單</h3>
                <p className="text-base leading-relaxed text-[#4A4A4A] font-sans">
                  填寫你的出生年月日、時間、地區，選擇命理類型（八字或紫微斗數），並設定 2-3 條關於你過去或現在的驗證問題。
                </p>
              </div>
            </div>
            
            <div className="flex gap-8 items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-sm bg-[#C93B2B] text-white flex items-center justify-center font-serif text-xl">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-serif text-[#1A1A1A] mb-3">命理師回答</h3>
                <p className="text-base leading-relaxed text-[#4A4A4A] font-sans">
                  多位命理師會看到你的求測單並回答你的驗證問題。每位命理師會根據你的出生資料和命理分析來回答。
                </p>
              </div>
            </div>
            
            <div className="flex gap-8 items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-sm bg-[#C93B2B] text-white flex items-center justify-center font-serif text-xl">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-serif text-[#1A1A1A] mb-3">比較與選擇</h3>
                <p className="text-base leading-relaxed text-[#4A4A4A] font-sans">
                  你可以並排比較不同命理師的回答，看看誰的分析最準確、最有洞察力，再決定選擇誰。
                </p>
              </div>
            </div>
            
            <div className="flex gap-8 items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-sm bg-[#C93B2B] text-white flex items-center justify-center font-serif text-xl">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-serif text-[#1A1A1A] mb-3">{free ? '選擇與諮詢' : '付款與諮詢'}</h3>
                <p className="text-base leading-relaxed text-[#4A4A4A] font-sans">
                  {free
                    ? '選定心水命理師後，即可開啟私訊通道，進行更深入的命理諮詢。'
                    : '選定後付款，即可開啟與命理師的私訊通道，進行更深入的命理諮詢。'}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
          <p className="text-xs tracking-[0.2em] uppercase text-[#C93B2B] font-bold font-sans text-center mb-12">
            常見問題
          </p>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white border border-[#E5E0D8] shadow-sm rounded-sm p-6 md:p-8">
              <h4 className="text-lg sm:text-xl font-serif text-[#1A1A1A] mb-2">我需要先付款嗎？</h4>
              <p className="text-sm text-[#5C5C5C] font-sans">
                {free
                  ? '不需要。平台目前免費，你可以先看齊多位命理師的回答，再揀最適合的一位開通諮詢。'
                  : '不需要。你可以先看到多位命理師的回答後再決定付款給其中一位。'}
              </p>
            </div>
            
            <div className="bg-white border border-[#E5E0D8] shadow-sm rounded-sm p-6 md:p-8">
              <h4 className="text-lg sm:text-xl font-serif text-[#1A1A1A] mb-2">驗證問題是什麼？</h4>
              <p className="text-sm text-[#5C5C5C] font-sans">
                驗證問題是關於你過去或現在的真實情況，例如個人特徵、人生經歷、家庭背景等。命理師透過你的出生資料來回答這些問題，讓你驗證他們的準確度。
              </p>
            </div>
            
            <div className="bg-white border border-[#E5E0D8] shadow-sm rounded-sm p-6 md:p-8">
              <h4 className="text-lg sm:text-xl font-serif text-[#1A1A1A] mb-2">我的個人資料安全嗎？</h4>
              <p className="text-sm text-[#5C5C5C] font-sans">
                {free
                  ? '是的。所有聯絡資料都不會公開，選定命理師前對方看不到你的聯絡方式，選定後亦只能在平台內私訊。'
                  : '是的。所有聯絡資料都不會公開，付款前命理師看不到你的聯絡方式。付款後你們只能在平台內私訊。'}
              </p>
            </div>
            
            <div className="bg-white border border-[#E5E0D8] shadow-sm rounded-sm p-6 md:p-8">
              <h4 className="text-lg sm:text-xl font-serif text-[#1A1A1A] mb-2">{free ? '需要收費嗎？' : '命理師如何收費？'}</h4>
              <p className="text-sm text-[#5C5C5C] font-sans">
                {free
                  ? '平台目前免費 —— 選定命理師後即可直接開通諮詢室。（日後若收費，命理師會在個人頁顯示價格範圍。）'
                  : '每位命理師會在個人頁顯示價格範圍。你可以在比較答案時看到具體費用，選定後付款。'}
              </p>
            </div>
          </div>
        </section>

        <footer className="px-6 md:px-12 lg:px-24 py-12 border-t border-[#E5E0D8] text-center">
          <p className="text-sm mb-2">
            <Link to="/privacy" className="text-[#5C5C5C] hover:text-[#C93B2B] hover:underline">隱私政策</Link>
            <span className="text-[#C9C2B5] mx-2">·</span>
            <Link to="/terms" className="text-[#5C5C5C] hover:text-[#C93B2B] hover:underline">服務條款</Link>
          </p>
          <p className="text-sm text-[#5C5C5C] font-sans">
            © 2026 命理配對平台 · 所有權利保留
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Home;